import { toast } from 'sonner';
import { captureAndReportError } from './errorReporting';
import {
  dimensionsMatch,
  getImageDimensions,
  getImageOnlyDimensions,
  getOrientation,
  type ImageDimensions,
} from './imageDimensions';
export interface EditorsChoiceValidationResult {
  isValid: boolean;
  error?: string;
}

/** Orientation a publisher picks in the dApp Preview toggle (no square option). */
export type PreviewOrientation = 'portrait' | 'landscape';

/**
 * Validates a preview upload. With a baseline, dimensions must match exactly;
 * for the first upload, `expectedOrientation` (from the Portrait/Landscape
 * toggle) is enforced instead.
 */
export const validatePreviewDimensions = async (
  file: File,
  baselineDimensions: ImageDimensions | null,
  expectedOrientation?: PreviewOrientation,
) => {
  try {
    const dimensions = await getImageDimensions(file);

    if (!baselineDimensions) {
      const actual = getOrientation(dimensions);
      if (
        expectedOrientation &&
        actual !== 'square' &&
        actual !== expectedOrientation
      ) {
        const ratio = expectedOrientation === 'portrait' ? '9:16' : '16:9';
        const error = `Upload a ${expectedOrientation} (${ratio}) preview, or switch the orientation toggle`;
        toast.error(error, {
          description: `Your file is ${dimensions.width}x${dimensions.height} pixels`,
        });
        return { isValid: false, error };
      }

      return {
        isValid: true,
        dimensions,
      };
    }

    if (!dimensionsMatch(dimensions, baselineDimensions)) {
      const error = `Media dimensions (${dimensions.width}x${dimensions.height}) don't match required dimensions (${baselineDimensions.width}x${baselineDimensions.height})`;
      toast.error(error);
      return {
        isValid: false,
        error,
      };
    }

    return {
      isValid: true,
      dimensions,
    };
  } catch (error) {
    const errorMsg = 'Failed to validate media dimensions';
    captureAndReportError(errorMsg, error);
    toast.error(errorMsg);
    return {
      isValid: false,
      error: errorMsg,
    };
  }
};

interface ExactDimensionValidationOptions {
  expectedWidth: number;
  expectedHeight: number;
  errorLabel: string;
}

const validateExactDimensions = async (
  file: File,
  options: ExactDimensionValidationOptions,
): Promise<EditorsChoiceValidationResult> => {
  try {
    const dimensions = await getImageOnlyDimensions(file);

    if (
      dimensions.width !== options.expectedWidth ||
      dimensions.height !== options.expectedHeight
    ) {
      const error = `${options.errorLabel} must be exactly ${options.expectedWidth}x${options.expectedHeight} pixels`;
      toast.error(error, {
        description: `Your image is ${dimensions.width}x${dimensions.height} pixels`,
      });
      return { isValid: false, error };
    }

    return { isValid: true };
  } catch {
    const error = `Failed to validate ${options.errorLabel.toLowerCase()} dimensions`;
    toast.error(error);
    return { isValid: false, error };
  }
};

export const validateEditorsChoiceGraphic = (
  file: File,
): Promise<EditorsChoiceValidationResult> => {
  return validateExactDimensions(file, {
    expectedWidth: 1200,
    expectedHeight: 1200,
    errorLabel: "Editor's Choice graphic",
  });
};

export const validateBannerDimensions = (
  file: File,
): Promise<EditorsChoiceValidationResult> => {
  return validateExactDimensions(file, {
    expectedWidth: 1200,
    expectedHeight: 600,
    errorLabel: 'Banner',
  });
};

export const hasAnyPreview = (
  previews: (File | null)[],
  previewUrls?: (string | null)[],
  excludeIndex?: number,
) => {
  const hasFilePreview = previews.some(
    (p, index) => p !== null && index !== excludeIndex,
  );

  const hasUrlPreview =
    previewUrls?.some((url, index) => url !== null && index !== excludeIndex) ??
    false;

  return hasFilePreview || hasUrlPreview;
};
